import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { useShopModuleReportStatsQuery } from "@/modules/merchant/presentation/api/queries";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronRight, Layers } from "lucide-react";
import { useMemo } from "react";
import { formatDateTimeLocal } from "@/shared/lib/date-time";

const PO_TO = "/app/shops/$shopId/purchase-orders" as const;
const CLAIMS_TO = "/app/shops/$shopId/product-claims" as const;
const SETTLEMENT_TO = "/app/shops/$shopId/settlement" as const;
const MARKETPLACE_TO = "/app/shops/$shopId/marketplace" as const;

const PO_LABELS: Record<string, string> = {
  draft: "ຮ່າງ",
  submitted: "ສົ່ງແລ້ວ",
  partially_received: "ຮັບບາງສ່ວນ",
  received: "ຮັບຄົບ",
  canceled: "ຍົກເລີກ",
};

const CLAIM_LABELS: Record<string, string> = {
  draft: "ຮ່າງ",
  submitted: "ສົ່ງແລ້ວ",
  under_review: "ກຳລັງພິຈາລະນາ",
  resolved: "ສຳເລັດ",
  rejected: "ບໍ່ຮັບ",
  canceled: "ຍົກເລີກ",
};

const ST_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າ",
  processing: "ກຳລັງດຳເນີນ",
  paid_out: "ຈ່າຍແລ້ວ",
  failed: "ລົ້ມເຫຼວ",
};

function sumValues(r: Record<string, number> | undefined): number {
  if (!r) return 0;
  return Object.values(r).reduce((a, b) => a + b, 0);
}

function StatusTable({
  labels,
  data,
}: {
  labels: Record<string, string>;
  data: Record<string, number>;
}) {
  const entries = Object.keys(labels).map((k) => [k, data[k] ?? 0] as const);
  return (
    <table className="w-full text-left text-sm">
      <tbody className="divide-y divide-border">
        {entries.map(([key, n]) => (
          <tr key={key}>
            <th className="py-1.5 pr-3 font-normal text-muted-foreground">
              {labels[key] ?? key}
            </th>
            <td className="py-1.5 text-right font-medium tabular-nums text-foreground">
              {n}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * P6+ — ສະຫຼຸບສະຖານະ PO / ເຄສ / settlement / marketplace
 */
export function ShopReportsModulesPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentShop } = useMerchantShops(shopId);
  const flags = currentShop?.featureFlags;
  const verified = currentShop?.verifiedLevel ?? null;
  const perms = currentShop?.permissions ?? [];
  const stats = useShopModuleReportStatsQuery(shopId);
  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);

  const canLinkPo =
    hasShopFeature(flags, "procurement") &&
    shopId != null &&
    canUseMerchantPermission(perms, MerchantPermissions.inventory.read, verified);
  const canLinkClaims =
    hasShopFeature(flags, "claims") &&
    shopId != null &&
    canUseMerchantPermission(perms, MerchantPermissions.claims.read, verified);
  const canLinkSettlement =
    hasShopFeature(flags, "settlement") &&
    shopId != null &&
    canUseMerchantPermission(
      perms,
      MerchantPermissions.settlement.read,
      verified,
    );
  const canLinkMarketplace =
    hasShopFeature(flags, "marketplace_sync") &&
    shopId != null &&
    canUseMerchantPermission(perms, MerchantPermissions.marketplace.read, verified);

  const d = stats.data;
  const hasP6Block =
    d?.purchaseOrdersByStatus != null ||
    d?.claimsByStatus != null ||
    d?.settlementsByStatus != null ||
    d?.marketplaceSync != null;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.pos.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.reportsModules(shopId)} />
      </Header>
      <Main>
        <div className="mb-6">
          <h1 className="font-heading font-semibold text-foreground text-xl">
            ລາຍງານໂມດູນອື່ນ
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            PO, ເຄສ, settlement, marketplace — ຕາມ feature ແລະ ສິດ
          </p>
        </div>

        {stats.isError ? (
          <p className="text-destructive text-sm">ໂຫຼດສະຫຼຸບບໍ່ສຳເລັດ</p>
        ) : null}

        {!stats.isLoading && !hasP6Block ? (
          <p className="text-muted-foreground text-sm">
            ບໍ່ມີຂໍ້ມູນໂມດູນທີ່ທ່ານເຂົ້າເຖິງ — ຫຼື ຟີເຈີຖືກປິດ/ສິດບໍ່ພໍ
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {d?.purchaseOrdersByStatus != null ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">ໃບສັ່ງຊື້ (PO)</CardTitle>
                  {canLinkPo ? (
                    <Link
                      to={PO_TO}
                      params={{ shopId: shopId ?? "" }}
                      className="inline-flex items-center gap-1 text-primary text-xs font-medium"
                    >
                      ເປີດລາຍການ
                      <ChevronRight className="size-3.5" aria-hidden />
                    </Link>
                  ) : null}
                </div>
                <p className="text-muted-foreground text-xs">
                  ລວມ {numberFmt.format(sumValues(d.purchaseOrdersByStatus))} ໃບ
                </p>
              </CardHeader>
              <CardContent>
                <StatusTable
                  labels={PO_LABELS}
                  data={d.purchaseOrdersByStatus}
                />
              </CardContent>
            </Card>
          ) : null}

          {d?.claimsByStatus != null ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">ເຄສສິນຄ້າມີບັນຫາ</CardTitle>
                  {canLinkClaims ? (
                    <Link
                      to={CLAIMS_TO}
                      params={{ shopId: shopId ?? "" }}
                      className="inline-flex items-center gap-1 text-primary text-xs font-medium"
                    >
                      ເປີດລາຍການ
                      <ChevronRight className="size-3.5" aria-hidden />
                    </Link>
                  ) : null}
                </div>
                <p className="text-muted-foreground text-xs">
                  ລວມ {numberFmt.format(sumValues(d.claimsByStatus))} ເຄສ (ຕາມ
                  ສິດ/ສາຂາ)
                </p>
              </CardHeader>
              <CardContent>
                <StatusTable
                  labels={CLAIM_LABELS}
                  data={d.claimsByStatus}
                />
              </CardContent>
            </Card>
          ) : null}

          {d?.settlementsByStatus != null ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">ຊຳລະ (Settlement)</CardTitle>
                  {canLinkSettlement ? (
                    <Link
                      to={SETTLEMENT_TO}
                      params={{ shopId: shopId ?? "" }}
                      className="inline-flex items-center gap-1 text-primary text-xs font-medium"
                    >
                      ເປີດໜ້າ
                      <ChevronRight className="size-3.5" aria-hidden />
                    </Link>
                  ) : null}
                </div>
                <p className="text-muted-foreground text-xs">
                  ລວມ {numberFmt.format(sumValues(d.settlementsByStatus))}{" "}
                  ລາຍການ
                </p>
              </CardHeader>
              <CardContent>
                <StatusTable
                  labels={ST_LABELS}
                  data={d.settlementsByStatus}
                />
              </CardContent>
            </Card>
          ) : null}

          {d?.marketplaceSync != null ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="inline-flex items-center gap-2 text-base">
                    <Layers className="size-4 text-primary" aria-hidden />
                    Marketplace
                  </CardTitle>
                  {canLinkMarketplace ? (
                    <Link
                      to={MARKETPLACE_TO}
                      params={{ shopId: shopId ?? "" }}
                      className="inline-flex items-center gap-1 text-primary text-xs font-medium"
                    >
                      จัดการ listing
                      <ChevronRight className="size-3.5" aria-hidden />
                    </Link>
                  ) : null}
                </div>
                <p className="text-muted-foreground text-xs">
                  ເຜີຍແຜ່ {numberFmt.format(d.marketplaceSync.published)} ລາຍການ
                  · ພ້ອມເຜີຍແຜ່ເພີ່ມ{" "}
                  {numberFmt.format(d.marketplaceSync.eligibleUnpublished)} SKU
                </p>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sync ລ່າສຸດ</span>
                  <span className="font-medium tabular-nums">
                    {d.marketplaceSync.lastSyncAt
                      ? formatDateTimeLocal(d.marketplaceSync.lastSyncAt)
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Sync error (24 ຊມ.)
                  </span>
                  <span
                    className={
                      d.marketplaceSync.errorCount > 0
                        ? "font-medium tabular-nums text-destructive"
                        : "font-medium tabular-nums text-foreground"
                    }
                  >
                    {numberFmt.format(d.marketplaceSync.errorCount)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
